import styles from "./DatasetSnapshots.module.scss";
import { SnapshotIcon } from "./Icons";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import VLButton from "../../../uikit/Buttons/VLButton";
import ContentBodyLoader from "../../MetadataSummary/Content/ContentBody/ContentBodyLoader";
import { SnapshotItem } from "./SnapshotItem";
import { useVisibility } from "../../../../hooks/useVisibility";
import { SnapshotModal } from "./SnapshotModal";
import { useListSnapshotsApiV1DatasetDatasetIdSnapshotGet } from "../../../../vl-api/vlComponents";
import classNames from "classnames";
import { DatasetSnapshotMetadata } from "../../../../vl-api/vlSchemas";
import { VLInput } from "../../../uikit/FormFields/VLInput/VLInput";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getIsTrainModelEnabled } from "../../../../redux/SingleDataset/selectors";
import { DATASET_SNAPSHOTS_CREATE } from "../../../../helpers/constants/filters";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { useUserPermission } from "../../../../hooks/useUserPermission";

export const DatasetSnapshots = ({
  datasetId,
  onClose,
}: {
  onClose: () => void;
  datasetId: string;
}) => {
  const { data: snapshots, isLoading } =
    useListSnapshotsApiV1DatasetDatasetIdSnapshotGet({
      pathParams: { datasetId },
    });
  const createSnapshotsBehavior = useFeatureFlagService(
    DATASET_SNAPSHOTS_CREATE,
  );
  const canCreateSnapshot = useUserPermission("can_create_snapshot");
  const modal = useVisibility();
  return (
    <div>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>
            <SnapshotIcon />
            <div className={styles.text}>Snapshots</div>
            <div className={styles.filler} />
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
          <div className={styles.sub}>Saved states of this dataset</div>
        </div>
        {canCreateSnapshot && (
          <VLButton
            type="primary"
            size="middle"
            className={styles.saveButton}
            onClick={modal.open}
            disabled={!createSnapshotsBehavior.enabled}
            tooltip={createSnapshotsBehavior.reason}
          >
            Create a snapshot
          </VLButton>
        )}
        <SnapshotModal {...modal} datasetId={datasetId} />
      </div>
      <div
        className={classNames(styles.wrapper, { [styles.loading]: isLoading })}
      >
        {isLoading ? (
          <ContentBodyLoader center size={48} />
        ) : (
          <>
            {!snapshots?.length ? (
              <EmptyState />
            ) : (
              <SnapshotsList
                snapshots={snapshots}
                datasetId={datasetId}
                onClose={onClose}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const EmptyState = () => {
  const isTrainModelEnabled = useSelector(getIsTrainModelEnabled);
  return (
    <div className={styles.empty}>
      No snapshots exist. You will see them when you’ll add more data
      {isTrainModelEnabled ? " or send the dataset to model training" : ""}
    </div>
  );
};

const SnapshotsList = ({
  snapshots,
  datasetId,
  onClose,
}: {
  onClose: () => void;
  datasetId: string;
  snapshots: DatasetSnapshotMetadata[];
}) => {
  const [search, setSearch] = useState("");
  const displayedSnapshots = useMemo(() => {
    if (!search) {
      return snapshots;
    }
    return snapshots.filter(
      (s) =>
        s.snapshot_name.toLowerCase().includes(search.toLowerCase()) ||
        s.description?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [snapshots, search]);

  const isAnyRestoring = useMemo(() => {
    return snapshots.some((s) => s.is_restoring);
  }, [snapshots]);

  return (
    <>
      <VLInput
        className={styles.input}
        placeholder="Search"
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {!displayedSnapshots.length && (
        <div className={styles.empty}>No snapshots matching search</div>
      )}
      <div className={classNames(styles.list)}>
        {displayedSnapshots.map((snapshot) => (
          <SnapshotItem
            datasetId={datasetId}
            snapshot={snapshot}
            onClose={onClose}
            isAnyRestoring={isAnyRestoring}
          />
        ))}
      </div>
    </>
  );
};
